import { useCallback, useEffect, useRef, useState } from "react";
import { isSameDay, isValid as isValidDate } from "date-fns";

import type { MarkerFormValues } from "./MarkerDialog.types";

/** Empty form state — recreated every time the dialog opens. */
const emptyValues = (): MarkerFormValues => ({
  type: "phaseChange",
  linkMode: "session",
  sessionId: "",
  markerDate: null,
  label: "",
  notes: "",
  applyToCurrentGoal: false,
  applyToOtherGoals: false,
  otherTargetIds: [],
  extraTargetIds: [],
  createMore: false,
});

/**
 * Drops what the marker's type can't carry, applied when the type changes.
 *
 * A phase line describes a change in this target's own teaching procedures, so
 * it's always anchored to one of its sessions and never shared with other
 * targets. Those fields are dropped rather than just hidden: the form keeps
 * what was typed when the type changes, so an external event's date and targets
 * would otherwise still be submitted.
 *
 * Only a type change triggers it, never opening a marker for edit: a phase line
 * stored with a date anchor — the previous UI allowed it — keeps that date, so
 * its label and notes stay editable and the marker isn't re-anchored behind the
 * clinician's back.
 *
 * `createMore` isn't touched — the dialog already offers it per variant and
 * leaves it out of a phase line's payload, so the checkbox keeps its state
 * while the clinician switches types.
 */
const applyTypeRules = (values: MarkerFormValues): MarkerFormValues =>
  values.type === "phaseChange"
    ? {
        ...values,
        linkMode: "session",
        markerDate: null,
        applyToCurrentGoal: false,
        applyToOtherGoals: false,
        otherTargetIds: [],
        extraTargetIds: [],
      }
    : values;

/** Same ids, regardless of order. */
const sameIds = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) return false;
  const ids = new Set(a);
  return b.every((id) => ids.has(id));
};

/** Whether both forms place the marker at the same spot on the chart. */
const sameAnchor = (a: MarkerFormValues, b: MarkerFormValues): boolean => {
  if (a.linkMode !== b.linkMode) return false;
  if (a.linkMode === "session") return a.sessionId === b.sessionId;
  if (!a.markerDate || !b.markerDate) return a.markerDate === b.markerDate;
  // Only the calendar day is sent, so a same-day pick isn't a change.
  return isSameDay(a.markerDate, b.markerDate);
};

/**
 * Whether both forms apply the marker to the same targets. `otherTargetIds`
 * only counts while the picker is on — a stale selection under an unticked
 * checkbox isn't sent. `extraTargetIds` isn't compared: the form can't change it.
 */
const sameTargets = (a: MarkerFormValues, b: MarkerFormValues): boolean =>
  a.applyToCurrentGoal === b.applyToCurrentGoal &&
  a.applyToOtherGoals === b.applyToOtherGoals &&
  (!a.applyToOtherGoals || sameIds(a.otherTargetIds, b.otherTargetIds));

interface UseMarkerFormOptions {
  /** Dialog visibility — the form resets on every open. */
  open: boolean;
  /** Whether the target has sessions to link a marker to (not needed for dates). */
  hasSessions: boolean;
  /** Bumped after a successful save to clear the entry fields (create more). */
  resetToken: number;
  /** Values an edit starts from. When omitted the form opens empty (create). */
  initialValues?: MarkerFormValues;
}

/**
 * Owns the marker form state: values, per-field updates, validity, and the
 * partial reset behind the "create more" flow.
 *
 * Both marker types require a position and a label: the vertical line alone
 * doesn't say what changed, and clinicians note the target phase in the label
 * when it applies. An external event is positioned by a session or a date; a
 * phase line only by a session (see {@link normalize}).
 *
 * @param options
 * @param options.open        - Resets the whole form whenever it flips to true
 * @param options.hasSessions - Guards session-linked submission when there are no sessions
 * @param options.resetToken  - Clears label/notes when it changes
 * @param [options.initialValues] - Values an edit starts from
 */
export const useMarkerForm = ({
  open,
  hasSessions,
  resetToken,
  initialValues,
}: UseMarkerFormOptions) => {
  const [values, setValues] = useState<MarkerFormValues>(() => initialValues ?? emptyValues());
  const labelInputRef = useRef<HTMLInputElement>(null);

  // Latest initialValues, read when the dialog opens rather than on every
  // change: a refetch handing over a new object mid-edit must not wipe what the
  // clinician is typing.
  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;

  // What an edit is compared against, frozen when the dialog opens.
  const [baseline, setBaseline] = useState(initialValues);

  // Reset the form every time the dialog opens — to the edited marker's values,
  // or empty when creating.
  useEffect(() => {
    if (!open) return;
    setValues(initialValuesRef.current ?? emptyValues());
    setBaseline(initialValuesRef.current);
  }, [open]);

  // "Create more": after a save the dialog stays open, so only the fields that
  // describe this marker are cleared. Type, session or date, the targets it
  // applies to and the checkbox carry over to the next entry, and focus returns
  // to the label to keep typing.
  const lastResetToken = useRef(resetToken);
  useEffect(() => {
    // Nothing to reset while closed — the open effect above already clears the form. 
    if (!open) {
      lastResetToken.current = resetToken;
      return;
    }

    if (resetToken === lastResetToken.current) return;

    lastResetToken.current = resetToken;

    setValues((prev) => ({ ...prev, label: "", notes: "" }));
    labelInputRef.current?.focus();
  }, [open, resetToken]);

  const setField = useCallback(
    <K extends keyof MarkerFormValues>(key: K, value: MarkerFormValues[K]) =>
      setValues((prev) => {
        const next = { ...prev, [key]: value };
        if (key === "type") return applyTypeRules(next);
        // Picking a session anchors the marker to it. That's how a phase line
        // stored with a date moves onto the timeline: it opens on its date and
        // only changes when the clinician chooses a session.
        if (key === "sessionId" && next.sessionId) {
          return { ...next, linkMode: "session", markerDate: null };
        }
        return next;
      }),
    []
  );

  // A date-linked marker doesn't need recorded sessions.
  const hasAnchor =
    values.linkMode === "date"
      ? values.markerDate != null && isValidDate(values.markerDate)
      : Boolean(values.sessionId) && hasSessions;
  const isValid = hasAnchor && values.label.trim().length > 0;

  // An edit is dirty when its position, targets, label or notes changed. Label
  // and notes are compared trimmed, as they're sent, so whitespace-only edits
  // don't enable saving a no-op update. A new marker is always worth submitting
  // once valid.
  const isDirty = baseline
    ? values.label.trim() !== baseline.label.trim() ||
      values.notes.trim() !== baseline.notes.trim() ||
      !sameAnchor(values, baseline) ||
      !sameTargets(values, baseline)
    : true;

  return { values, setField, isValid, isDirty, labelInputRef };
};
